import { useCallback } from 'react';
import type { ApolloQueryResult } from 'apollo-client';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { useQueryInterop, type UseQueryInteropSupportedHookOptions } from './useQueryInterop.ts';

/**
 * Returns an imperative query function that can be called on-demand,
 * rather than executing automatically on render like useQueryInterop.
 *
 * Follows the same pattern as page-tree sidebar's useImperativeQuery.
 *
 * @example
 * const fetchData = useImperativeQueryInterop(MyQuery);
 * const result = await fetchData({ id: '123' });
 */
export const useImperativeQueryInterop = <TQuery, TVars extends Record<string, any>>(
	query: any,
	options?: UseQueryInteropSupportedHookOptions<TQuery, TVars>,
): ((variables: TVars) => Promise<
	Pick<ApolloQueryResult<TQuery>, 'data'> & {
		error?: ApolloErrorInterop;
	}
>) => {
	const { refetch } = useQueryInterop<TQuery, TVars>(query, { ...options, skip: true });

	return useCallback((variables: TVars) => refetch(variables), [refetch]);
};
